/**
 * PixelLogo
 *
 * Interactive pixel animation of the YallyHub star logo.
 * Loads the actual logo image (/logo512.png), samples it into a pixel grid,
 * then renders each filled cell as an animated particle with:
 *   - Fly-in entrance (spring physics from scattered positions)
 *   - Continuous diagonal color wave rippling through the logo
 *   - Subtle positional breathing
 *   - Mouse repulsion (particles scatter and spring back)
 *
 * Usage:
 *   <PixelLogo />
 *   <PixelLogo size={400} text="Laden..." fullPage />
 *   <PixelLogo color="#E8C84A" />
 */

import { useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';

// ─── Props ───────────────────────────────────────────────────────────────────

interface PixelLogoProps {
  /** Canvas display size in px. Default 340. */
  size?: number;
  /** Base pixel color (hex). Default #5B7304 (moss-primary). */
  color?: string;
  /** Optional text below the animation. */
  text?: string;
  /** Whether pixels react to mouse. Default true. */
  interactive?: boolean;
  /** Centered loading screen layout (min-h-[50vh]). */
  fullPage?: boolean;
  /** Additional className for the wrapper div. */
  className?: string;
}

// ─── Particle type ───────────────────────────────────────────────────────────

interface Particle {
  x: number;
  y: number;
  targetX: number;
  targetY: number;
  vx: number;
  vy: number;
  size: number;
  /** Pre-computed wave offset based on grid position (diagonal) */
  wave: number;
}

// ─── Constants ───────────────────────────────────────────────────────────────

/** Sampling resolution — how many cells to divide the logo into */
const GRID = 100;

/** Fraction of each cell that's filled (< 1 leaves pixel-art gaps) */
const FILL = 0.78;

// ─── Helpers ─────────────────────────────────────────────────────────────────

function parseHex(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}

function lighten(
  [r, g, b]: [number, number, number],
  amount: number,
): [number, number, number] {
  return [
    Math.min(255, Math.round(r + (255 - r) * amount)),
    Math.min(255, Math.round(g + (255 - g) * amount)),
    Math.min(255, Math.round(b + (255 - b) * amount)),
  ];
}

/**
 * Load the actual logo image and sample it into a boolean grid.
 * The logo is dark on a pink background — we detect dark pixels.
 */
function sampleLogoImage(): Promise<boolean[][]> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = GRID;
      canvas.height = GRID;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0, GRID, GRID);

      const { data } = ctx.getImageData(0, 0, GRID, GRID);
      const grid: boolean[][] = [];

      for (let y = 0; y < GRID; y++) {
        grid[y] = [];
        for (let x = 0; x < GRID; x++) {
          const i = (y * GRID + x) * 4;
          // Luminance check — logo pixels are dark green, bg is pink/light
          const lum = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
          grid[y][x] = lum < 100;
        }
      }

      resolve(grid);
    };

    img.onerror = () => {
      // Fallback: empty grid (component will just show text)
      console.error('[PixelLogo] Failed to load /logo512.png');
      resolve(
        Array.from({ length: GRID }, () => Array(GRID).fill(false)),
      );
    };

    img.src = '/logo512.png';
  });
}

// ─── Component ───────────────────────────────────────────────────────────────

export const PixelLogo = ({
  size = 340,
  color = '#5B7304',
  text,
  interactive = true,
  fullPage = false,
  className,
}: PixelLogoProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const stateRef = useRef<{
    particles: Particle[];
    mouse: { x: number; y: number; active: boolean };
    frame: number;
    t0: number;
    base: [number, number, number];
    high: [number, number, number];
  } | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let cancelled = false;

    // High-DPI
    const dpr = window.devicePixelRatio || 1;
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    canvas.style.width = `${size}px`;
    canvas.style.height = `${size}px`;

    const base = parseHex(color);
    const high = lighten(base, 0.42);

    sampleLogoImage().then((shape) => {
      if (cancelled) return;

      // Build particles from sampled shape
      const cell = size / GRID;
      const pxSize = cell * FILL;
      const particles: Particle[] = [];

      for (let row = 0; row < GRID; row++) {
        for (let col = 0; col < GRID; col++) {
          if (!shape[row][col]) continue;

          const tx = col * cell + cell / 2;
          const ty = row * cell + cell / 2;

          // Start scattered
          const a = Math.random() * Math.PI * 2;
          const d = size * 0.4 + Math.random() * size * 0.5;

          particles.push({
            x: size / 2 + Math.cos(a) * d,
            y: size / 2 + Math.sin(a) * d,
            targetX: tx,
            targetY: ty,
            vx: 0,
            vy: 0,
            size: pxSize,
            wave: col * 0.10 + row * 0.07,
          });
        }
      }

      stateRef.current = {
        particles,
        mouse: { x: 0, y: 0, active: false },
        frame: 0,
        t0: performance.now(),
        base,
        high,
      };

      // ── Animation ─────────────────────────────────────────────────
      const mouseR = size * 0.15;
      const mouseR2 = mouseR * mouseR;

      const loop = () => {
        const s = stateRef.current;
        if (!s || cancelled) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const t = (performance.now() - s.t0) / 1000;

        ctx.clearRect(0, 0, size * dpr, size * dpr);
        ctx.save();
        ctx.scale(dpr, dpr);

        const { mouse, base: bRgb, high: hRgb, particles: pts } = s;

        for (let i = 0; i < pts.length; i++) {
          const p = pts[i];

          // Subtle breathing oscillation
          const bx = Math.sin(t * 0.9 + p.wave * 2.7) * 0.5;
          const by = Math.cos(t * 0.7 + p.wave * 3.3) * 0.5;

          // Spring toward target + breathing offset
          p.vx += (p.targetX + bx - p.x) * 0.07;
          p.vy += (p.targetY + by - p.y) * 0.07;

          // Mouse repulsion
          if (interactive && mouse.active) {
            const dx = p.x - mouse.x;
            const dy = p.y - mouse.y;
            const d2 = dx * dx + dy * dy;
            if (d2 < mouseR2 && d2 > 1) {
              const dist = Math.sqrt(d2);
              const f = ((mouseR - dist) / mouseR);
              const push = f * f * 14;
              p.vx += (dx / dist) * push;
              p.vy += (dy / dist) * push;
            }
          }

          p.vx *= 0.83;
          p.vy *= 0.83;
          p.x += p.vx;
          p.y += p.vy;

          // Color wave — diagonal sweep
          const w = (Math.sin(p.wave - t * 2.0) + 1) * 0.5;
          const r = bRgb[0] + (hRgb[0] - bRgb[0]) * w;
          const g = bRgb[1] + (hRgb[1] - bRgb[1]) * w;
          const b = bRgb[2] + (hRgb[2] - bRgb[2]) * w;

          ctx.globalAlpha = 0.80 + w * 0.20;
          ctx.fillStyle = `rgb(${r | 0},${g | 0},${b | 0})`;
          ctx.fillRect(
            (p.x - p.size / 2) | 0,
            (p.y - p.size / 2) | 0,
            p.size,
            p.size,
          );
        }

        ctx.restore();
        s.frame = requestAnimationFrame(loop);
      };

      stateRef.current.frame = requestAnimationFrame(loop);
    });

    return () => {
      cancelled = true;
      if (stateRef.current) {
        cancelAnimationFrame(stateRef.current.frame);
        stateRef.current = null;
      }
    };
  }, [size, color, interactive]);

  // ── Mouse handlers ──────────────────────────────────────────────────────

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!interactive || !stateRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    stateRef.current.mouse = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      active: true,
    };
  };

  const handleMouseLeave = () => {
    if (stateRef.current) stateRef.current.mouse.active = false;
  };

  // ── Render ──────────────────────────────────────────────────────────────

  const content = (
    <div className={cn('flex flex-col items-center gap-5', className)}>
      <canvas
        ref={canvasRef}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        style={{ width: size, height: size }}
        className="cursor-default"
      />
      {text && (
        <p className="text-base text-neutral-secondary-mid animate-pulse">
          {text}
        </p>
      )}
    </div>
  );

  if (fullPage) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        {content}
      </div>
    );
  }

  return content;
};
